import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Newspaper, Eye, Heart, PlayCircle, ChevronRight, PenLine } from "lucide-react";
import { API_URL } from "@/config/api";

export const CATEGORY_LABELS = {
  news: { ru: "Новости", en: "News", zh: "新闻" },
  lessons: { ru: "Обучение", en: "Lessons", zh: "教学" },
  openings: { ru: "Дебюты", en: "Openings", zh: "开局" },
  endgames: { ru: "Эндшпиль", en: "Endgames", zh: "残局" },
  tactics: { ru: "Тактика", en: "Tactics", zh: "战术" },
  tournaments: { ru: "Турниры", en: "Tournaments", zh: "锦标赛" },
  players: { ru: "Шахматисты", en: "Players", zh: "棋手" },
};

export const catLabel = (cat, lang) => CATEGORY_LABELS[cat]?.[lang] || CATEGORY_LABELS[cat]?.en || cat;
export const mediaUrl = (u) => (u && u.startsWith("/api/") ? `${API_URL}${u}` : u);
export const fmtDate = (iso, lang) => new Date(iso).toLocaleDateString(lang === "ru" ? "ru-RU" : lang === "zh" ? "zh-CN" : "en-US", { day: "numeric", month: "short" });

export const PostCard = ({ post, lang, compact }) => {
  const navigate = useNavigate();
  return (
    <div
      onClick={() => navigate(`/blog/${post.slug}`)}
      data-testid={`post-card-${post.id}`}
      className={`group relative shrink-0 cursor-pointer overflow-hidden rounded-xl border border-border/50 bg-card/60 transition-transform active:scale-[0.98] ${compact ? "w-[260px] sm:w-[300px]" : "w-full"}`}
    >
      <div className="relative aspect-[16/9] bg-muted/40 overflow-hidden">
        {post.cover_url ? (
          <img src={mediaUrl(post.cover_url)} alt="" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
        ) : (
          <div className="h-full w-full flex items-center justify-center bg-gradient-to-br from-primary/25 via-background to-primary/5"><Newspaper className="h-10 w-10 text-primary/60" /></div>
        )}
        {post.video_url && <PlayCircle className="absolute right-2 top-2 h-6 w-6 text-white drop-shadow" />}
        <span className="absolute left-2 top-2 rounded-full bg-black/60 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">{catLabel(post.category, lang)}</span>
      </div>
      <div className="p-3 space-y-1">
        <p className="text-[11px] text-muted-foreground flex items-center gap-2">
          <span className="text-primary font-semibold">{post.author_title ? `${post.author_title} ` : ""}{post.author_username}</span>
          <span>·</span><span>{fmtDate(post.published_at || post.created_at, lang)}</span>
        </p>
        <h3 className="font-bold text-sm leading-snug line-clamp-2">{post.title}</h3>
        {!compact && post.excerpt && <p className="text-xs text-muted-foreground line-clamp-2">{post.excerpt}</p>}
        <p className="text-[11px] text-muted-foreground flex items-center gap-3 pt-1">
          <span className="flex items-center gap-1"><Eye className="h-3 w-3" />{post.views || 0}</span>
          <span className="flex items-center gap-1"><Heart className={`h-3 w-3 ${post.liked ? "fill-red-500 text-red-500" : ""}`} />{post.likes_count || 0}</span>
        </p>
      </div>
    </div>
  );
};

export const BlogFeed = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    fetch(`${API_URL}/api/blog/posts?limit=8`).then((r) => r.json()).then((d) => setPosts(d.posts || [])).catch(() => {});
  }, []);

  return (
    <section className="space-y-3" data-testid="blog-feed">
      <div className="flex items-center justify-between px-1">
        <h2 className="text-base md:text-lg font-bold flex items-center gap-2"><Newspaper className="h-5 w-5 text-primary" />{t("blog.feedTitle")}</h2>
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/blog/new")} className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1" data-testid="blog-write-btn">
            <PenLine className="h-3.5 w-3.5" />{t("blog.write")}
          </button>
          <button onClick={() => navigate("/blog")} className="text-xs text-primary flex items-center gap-0.5" data-testid="blog-all-btn">
            {t("blog.all")}<ChevronRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      {posts.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border/60 p-6 text-center text-sm text-muted-foreground" data-testid="blog-empty">{t("blog.empty")}</div>
      ) : (
        <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 snap-x" style={{ scrollbarWidth: "none" }}>
          {posts.map((p) => <div key={p.id} className="snap-start"><PostCard post={p} lang={i18n.language} compact /></div>)}
        </div>
      )}
    </section>
  );
};

export default BlogFeed;
